import { useQuery } from "@tanstack/react-query";
import { coachApi } from "@bw/shared/api";
import type {
  ClubCoachProfilePublicResponse,
  CoachLessonServiceResponse,
} from "@bw/shared/types/generated";
import { queryKeys } from "../../../common/query/queryClient";
import { clubCoachRatingApi } from "../ratingApi";

/**
 * Club profile and facility-scoped catalog reads are independently cached.
 * Portable experience is intentionally not mixed into this club-owned surface.
 */

export function useCoachProfile(
  coachProfileId: string | undefined,
  facilityId?: string,
) {
  return useQuery<ClubCoachProfilePublicResponse>({
    queryKey: [
      ...queryKeys.coaches.coach(coachProfileId ?? ""),
      facilityId ?? "",
    ],
    queryFn: () => coachApi.getCoach(coachProfileId as string, facilityId),
    enabled: Boolean(coachProfileId),
  });
}

export function useCoachServices(
  coachProfileId: string | undefined,
  facilityId: string | undefined,
) {
  return useQuery<CoachLessonServiceResponse[]>({
    queryKey: [
      ...queryKeys.coaches.services(coachProfileId ?? ""),
      facilityId ?? "",
    ],
    queryFn: () =>
      coachApi.getCoachServices(coachProfileId as string, facilityId as string),
    enabled: Boolean(coachProfileId && facilityId),
  });
}

export function useCoachRatings(
  coachProfileId: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: [...queryKeys.coaches.coach(coachProfileId ?? ""), "ratings"],
    queryFn: () => clubCoachRatingApi.list(coachProfileId as string),
    enabled: enabled && Boolean(coachProfileId),
  });
}
